import { Injectable } from '@angular/core';
import * as Az from '@azure/storage-blob';

function ensureCryptoRandomUUID(): void {
  if (typeof globalThis.crypto === 'undefined') return;
  if (typeof globalThis.crypto.randomUUID === 'function') return;

  globalThis.crypto.randomUUID =
    (): `${string}-${string}-${string}-${string}-${string}` => {
      const bytes = new Uint8Array(16);
      globalThis.crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
    };
}

ensureCryptoRandomUUID();

@Injectable({
  providedIn: 'root',
})
export class AzureStorageService {
  private sasToken: string = '';
  private azureBlobEndpoint: string = '';
  private blobServiceClient: any = '';
  private isInitialized: boolean = false;

  constructor() {}

  async init(settings: any, containerName: string): Promise<boolean> {
    try {
      const url = `${settings.azureFunctionsEndpoint}/GetAzureTableAccessTokens`;
      const httpResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'X-FUNCTIONS-KEY': settings.azureAccessToken,
        },
      });

      if (!httpResponse.ok) {
        throw new Error(
          `HTTP ${httpResponse.status}: ${httpResponse.statusText}`,
        );
      }

      const response = await httpResponse.json();
      const tokenData = response.find((r: any) => r.name === containerName);
      if (!tokenData) {
        throw new Error(`Container '${containerName}' not found in response`);
      }

      this.setBlobServiceClient(
        settings.azureBlobEndpoint,
        tokenData.accessToken,
      );
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('Azure Storage init error:', error);
      this.isInitialized = false;
      return false;
    }
  }

  private setBlobServiceClient(url: string, sasToken: string) {
    this.sasToken = sasToken;
    // Initialize BlobServiceClient with SAS URL
    this.azureBlobEndpoint = url;
    const blobUrl = `${url}/?${this.sasToken}`;
    this.blobServiceClient = new Az.BlobServiceClient(blobUrl);
  }

  private ensureInitialized(): boolean {
    return this.isInitialized && this.blobServiceClient;
  }

  /** Upload File to Azure Storage */
  async uploadFile(
    containerName: string,
    blobName: string,
    blobData: unknown,
  ): Promise<string> {
    if (!this.ensureInitialized()) return '';

    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlockBlobClient(blobName);
      await blobClient.uploadData(blobData);
      return blobClient.url; // Return the uploaded file URL
    } catch (error) {
      console.error('Azure Storage upload error:', error);
      return '';
    }
  }

  /** List Blobs in Container */
  async listBlobs(containerName: string): Promise<string[]> {
    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      let blobUrls: string[] = [];
      for await (const blob of containerClient.listBlobsFlat()) {
        const blobUrl = `${this.azureBlobEndpoint}/${containerName}/${blob.name}`;
        blobUrls.push(blobUrl);
      }
      return blobUrls;
    } catch (error) {
      console.error('Azure Storage list blobs error:', error);
      return [];
    }
  }

  async downloadBlobAsText(
    containerName: string,
    blobName: string,
  ): Promise<string | null> {
    if (!this.ensureInitialized()) return '';

    try {
      const containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      const blobClient = containerClient.getBlockBlobClient(blobName);
      const blobExists = await blobClient.exists();

      if (!blobExists) return '';

      const response = await blobClient.download();
      const blobData = await response.blobBody;
      return blobData ? await blobData.text() : ''; // Convert blob to text
    } catch (error) {
      console.error('Azure Storage download error:', error);
      return null;
    }
  }
}
